import { Component, OnInit } from '@angular/core';
import { MailService } from '../services/mail.service';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { fadeInUp400ms } from '@vex/animations/fade-in-up.animation';
import { stagger40ms } from '@vex/animations/stagger.animation';
import { trackById } from '@vex/utils/track-by';
import { SelectionModel } from '@angular/cdk/collections';
import { Mail } from '../interfaces/mail.interface';
import {
  MatCheckboxChange,
  MatCheckboxModule
} from '@angular/material/checkbox';
import { MailListEntryComponent } from '../components/mail-list-entry/mail-list-entry.component';
import { VexScrollbarComponent } from '@vex/components/vex-scrollbar/vex-scrollbar.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { MailViewComponent } from "./mail-view.component";
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { MailBulkConfirmDialogComponent } from '../components/mail-bulk-confirm-dialog/mail-bulk-confirm-dialog.component';
import { MailLabelDialogComponent } from '../components/mail-label-dialog/mail-label-dialog.component';

@Component({
    selector: 'vex-mail-list',
    templateUrl: './mail-list.component.html',
    styleUrls: ['./mail-list.component.scss'],
    animations: [fadeInUp400ms, stagger40ms],
    imports: [
        NgIf,
        MatCheckboxModule,
        MatButtonModule,
        MatTooltipModule,
        MatIconModule,
        MatMenuModule,
        VexScrollbarComponent,
        NgFor,
        MailListEntryComponent,
        AsyncPipe,
        MailViewComponent
    ]
})
export class MailListComponent implements OnInit {
  mails$ = this.mailService.filteredMails$;
  gtSm$ = this.layoutService.gtSm$;
  selectedMail$ = this.mailService.selectedMail.asObservable();
  viewMode$ = this.mailService.viewMode.asObservable();
  selectedFolder$ = this.mailService.selectedFolder.asObservable();
  readFilter$ = this.mailService.readFilter.asObservable();
  currentPage$ = this.mailService.currentPage.asObservable();
  availableLabels$ = this.mailService.availableLabels.asObservable();

  trackById = trackById;

  selection = new SelectionModel<Mail['id']>(true, []);

  constructor(
    public mailService: MailService,
    private layoutService: VexLayoutService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {}

  refresh() {
    this.mailService.loadMessages();
  }

  nextPage() {
    this.selection.clear();
    this.mailService.nextPage();
  }

  prevPage() {
    this.selection.clear();
    this.mailService.prevPage();
  }

  setViewMode(mode: 'split' | 'focus') {
    if (this.mailService.selectedFolder.value === 'DRAFT' && mode === 'split') {
      this.mailService.setViewMode('focus');
      this.selection.clear();
      return;
    }

    this.mailService.setViewMode(mode);
    if (mode === 'focus') {
      this.selection.clear();
    }
  }

  isDraftFolder() {
    return this.mailService.selectedFolder.value === 'DRAFT';
  }

  setReadFilter(filter: 'all' | 'unread' | 'read') {
    this.mailService.setReadFilter(filter);
  }

  backToList() {
    this.mailService.clearSelectedMail();
  }

  selectedIds() {
    return this.selection.selected as number[];
  }

  markSelected(read: boolean) {
    this.mailService.markSelectionRead(this.selectedIds(), read);
    this.selection.clear();
  }

  moveSelected(folder: string) {
    this.mailService.moveSelection(this.selectedIds(), folder);
    this.selection.clear();
  }

  openLabelDialog() {
    const ref = this.dialog.open(MailLabelDialogComponent, {
      data: {
        labels: this.mailService.availableLabels.value,
        count: this.selection.selected.length
      },
      panelClass: 'vex-dialog-glossy'
    });

    ref.afterClosed().subscribe((labels: string[] | undefined) => {
      if (!labels?.length) {
        return;
      }
      this.mailService.assignLabels(this.selectedIds(), labels);
      this.selection.clear();
    });
  }

  confirmDelete() {
    const ref = this.dialog.open(MailBulkConfirmDialogComponent, {
      data: {
        title: 'Delete selected mails?',
        message: `${this.selection.selected.length} selected mails will be moved to Trash.`,
        confirmText: 'Move to Trash'
      },
      panelClass: 'vex-dialog-glossy'
    });

    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }
      this.moveSelected('TRASH');
    });
  }

  masterToggle(mails: Mail[] | null, change: MatCheckboxChange) {
    if (!mails) {
      return;
    }

    if (change.checked) {
      this.selection.select(...mails.map((mail) => mail.id));
    } else {
      this.selection.deselect(...mails.map((mail) => mail.id));
    }
  }

  isAllSelected(mails: Mail[]): boolean {
    return (
      mails?.length > 0 && mails?.length === this.selection.selected?.length
    );
  }

  isSomeButNotAllSelected(mails: Mail[]): boolean {
    return !this.isAllSelected(mails) && this.selection.hasValue();
  }
}
